import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  CheckCircle,
  RefreshCw,
  Truck,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Users,
  Package,
} from "lucide-react";
import { loadingPlansApi } from "@/services/loading-plan-api";
import { LoadingPlanMetricsCard } from "@/components/loading-plan/LoadingPlanMetricsCard";
import { PalletCard } from "@/components/loading-plan/PalletCard";
import { OrderGroupsSidebar } from "@/components/loading-plan/OrderGroupsSidebar";
import type { LoadingPlanResponse } from "@/types/loading-plan";

export function LoadingPlanView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loadingPlan, setLoadingPlan] = useState<LoadingPlanResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedVirtualPalletId, setSelectedVirtualPalletId] = useState<
    string | null
  >(null);
  const [palletCode, setPalletCode] = useState("");
  const [converting, setConverting] = useState(false);
  const [groupsExpanded, setGroupsExpanded] = useState(false);

  useEffect(() => {
    if (id) {
      loadLoadingPlan();
    }
  }, [id]);

  const loadLoadingPlan = async () => {
    try {
      setLoading(true);
      setError(null);
      const plan = await loadingPlansApi.getById(id!);
      setLoadingPlan(plan);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error al cargar el plan de carga",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async (orderGroups?: string[][], groupAlgorithms?: (string | null)[]) => {
    if (!id || !loadingPlan) return;

    try {
      setLoading(true);
      await loadingPlansApi.recalculate(
        id,
        orderGroups ?? loadingPlan.orderGroups,
        groupAlgorithms ?? loadingPlan.groupAlgorithms ?? undefined,
      );
      await loadLoadingPlan();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error al recalcular el plan",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!id) return;

    try {
      setLoading(true);
      await loadingPlansApi.approve(id);
      await loadLoadingPlan();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al aprobar el plan");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!id) return;

    try {
      setLoading(true);
      await loadingPlansApi.complete(id);
      await loadLoadingPlan();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error al completar el plan",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConvertClick = (virtualPalletId: string) => {
    setSelectedVirtualPalletId(virtualPalletId);
    setConvertDialogOpen(true);
  };

  const handleConvertConfirm = async () => {
    if (!selectedVirtualPalletId || !palletCode.trim()) return;

    try {
      setConverting(true);
      await loadingPlansApi.convertVirtualToPhysical(selectedVirtualPalletId, {
        palletCode: palletCode.trim(),
      });
      await loadLoadingPlan();
      setConvertDialogOpen(false);
      setPalletCode("");
      setSelectedVirtualPalletId(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error al convertir el palet",
      );
    } finally {
      setConverting(false);
    }
  };

  if (loading && !loadingPlan) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Cargando plan de carga...</p>
        </div>
      </div>
    );
  }

  if (error && !loadingPlan) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!loadingPlan) return null;

  const statusColors = {
    Draft: "bg-gray-500",
    Calculated: "bg-blue-500",
    Approved: "bg-green-500",
    InProgress: "bg-yellow-500",
    Completed: "bg-purple-500",
    Outdated: "bg-orange-500",
  };

  const isEditable =
    loadingPlan.status === "Draft" ||
    loadingPlan.status === "Calculated" ||
    loadingPlan.status === "Outdated";

  return (
    <>
      {/* Plan header – full width */}
      <div className="p-6 pb-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate(-1)} variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold">Plan de Carga</h1>
                <p className="text-muted-foreground">
                  Ruta: {loadingPlan.routeName} •{" "}
                  {new Date(loadingPlan.plannedDate).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Badge className={statusColors[loadingPlan.status]}>
                {loadingPlan.status === "Outdated"
                  ? "Desactualizado"
                  : loadingPlan.status}
              </Badge>

              <Badge
                variant={loadingPlan.orderGroups ? "default" : "secondary"}
                className={
                  loadingPlan.orderGroups
                    ? "bg-orange-500 hover:bg-orange-600"
                    : ""
                }
              >
                {loadingPlan.orderGroups
                  ? "Pedidos Mezclados"
                  : "Palets Exclusivos"}
              </Badge>

              {(loadingPlan.status === "Calculated" ||
                loadingPlan.status === "Outdated") && (
                <>
                  <Button
                    onClick={() => handleRecalculate()}
                    variant={
                      loadingPlan.status === "Outdated"
                        ? "destructive"
                        : "outline"
                    }
                    disabled={loading}
                  >
                    <RefreshCw
                      className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                    />
                    {loadingPlan.status === "Outdated"
                      ? "Actualizar Plan"
                      : "Recalcular"}
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={loading || loadingPlan.status === "Outdated"}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Aprobar
                  </Button>
                </>
              )}

              {loadingPlan.status === "Approved" && (
                <Button onClick={handleComplete} disabled={loading}>
                  <Truck className="h-4 w-4 mr-2" />
                  Marcar como Completado
                </Button>
              )}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <Card className="border-red-200 bg-red-50 dark:bg-red-950">
              <CardContent className="pt-6">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </CardContent>
            </Card>
          )}
      </div>

      {/* ── EDITABLE: compact metrics + dos columnas (grupos ← resultado →) ── */}
      {isEditable ? (
        <div className="flex flex-col px-6 gap-4 h-[calc(100vh-13.8rem)]">
          {/* Compact metrics strip */}
          <div className="shrink-0">
            <LoadingPlanMetricsCard
              compact
              metrics={loadingPlan.metrics}
              vehicleName={loadingPlan.vehicleName}
              palletTypeName={loadingPlan.palletTypeName}
            />
          </div>

          {/* Two-column area */}
          <div className="flex gap-6 flex-1 min-h-0">
            {/* Izquierda: editor de grupos de pedidos */}
            <div className="w-96 shrink-0 border rounded-xl bg-background shadow-sm overflow-hidden flex flex-col">
              <div className="flex items-center gap-2 px-4 py-3 border-b bg-muted/20 shrink-0">
                <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-semibold">Grupos de Pedidos</span>
                {(loadingPlan.orderGroups?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-auto shrink-0">
                    {loadingPlan.orderGroups!.length}
                  </Badge>
                )}
              </div>
              <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                <OrderGroupsSidebar
                  loadingPlan={loadingPlan}
                  onApplyGroups={handleRecalculate}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Derecha: resultado del cálculo (palets) */}
            <div className="flex-1 min-w-0 overflow-y-auto space-y-6 pr-1">
              {loadingPlan.virtualPallets.length === 0 &&
              loadingPlan.physicalPallets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 border-2 border-dashed rounded-xl text-muted-foreground">
                  <Package className="h-10 w-10 mb-3 opacity-30" />
                  <p className="text-sm">Configura los grupos y pulsa «Aplicar y Recalcular».</p>
                </div>
              ) : (
                <>
                  {loadingPlan.virtualPallets.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold mb-3">
                        Palets Virtuales ({loadingPlan.virtualPallets.length})
                      </h2>
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {loadingPlan.virtualPallets
                          .sort((a, b) => a.loadingOrder - b.loadingOrder)
                          .map((pallet) => (
                            <PalletCard
                              key={pallet.id}
                              pallet={pallet}
                              type="virtual"
                              onConvert={handleConvertClick}
                            />
                          ))}
                      </div>
                    </div>
                  )}
                  {loadingPlan.physicalPallets.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold mb-3">
                        Palets Físicos ({loadingPlan.physicalPallets.length})
                      </h2>
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {loadingPlan.physicalPallets
                          .sort((a, b) => a.loadingOrder - b.loadingOrder)
                          .map((pallet) => (
                            <PalletCard
                              key={pallet.id}
                              pallet={pallet}
                              type="physical"
                            />
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── EJECUCIÓN: métricas completas + palets full-width + grupos colapsados ── */
        <div className="px-6 pb-8 space-y-6">
          <LoadingPlanMetricsCard
            metrics={loadingPlan.metrics}
            vehicleName={loadingPlan.vehicleName}
            palletTypeName={loadingPlan.palletTypeName}
          />

          {loadingPlan.virtualPallets.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4">
                Palets Virtuales ({loadingPlan.virtualPallets.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {loadingPlan.virtualPallets
                  .sort((a, b) => a.loadingOrder - b.loadingOrder)
                  .map((pallet) => (
                    <PalletCard
                      key={pallet.id}
                      pallet={pallet}
                      type="virtual"
                      onConvert={handleConvertClick}
                    />
                  ))}
              </div>
            </div>
          )}

          {loadingPlan.physicalPallets.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4">
                Palets Físicos ({loadingPlan.physicalPallets.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {loadingPlan.physicalPallets
                  .sort((a, b) => a.loadingOrder - b.loadingOrder)
                  .map((pallet) => (
                    <PalletCard
                      key={pallet.id}
                      pallet={pallet}
                      type="physical"
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Grupos de pedidos – acordeón colapsado (solo lectura) */}
          <div className="border rounded-xl overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-5 py-3 bg-muted/20 hover:bg-muted/40 transition-colors text-left"
              onClick={() => setGroupsExpanded((v) => !v)}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Grupos de Pedidos</span>
                {(loadingPlan.orderGroups?.length ?? 0) > 0 && (
                  <Badge variant="secondary">
                    {loadingPlan.orderGroups!.length}
                  </Badge>
                )}
              </div>
              {groupsExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            {groupsExpanded && (
              <div className="p-4 text-sm text-muted-foreground text-center border-t">
                El plan ya ha sido{" "}
                {loadingPlan.status === "Approved"
                  ? "aprobado"
                  : loadingPlan.status === "Completed"
                    ? "completado"
                    : "procesado"}.
                <br />
                No se pueden modificar los grupos en este estado.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Convert Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convertir Palet Virtual a Físico</DialogTitle>
            <DialogDescription>
              Introduce el código del palet físico que se ha preparado.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="palletCode">Código del Palet</Label>
              <Input
                id="palletCode"
                placeholder="Ej: PAL-001"
                value={palletCode}
                onChange={(e) => setPalletCode(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConvertDialogOpen(false);
                setPalletCode("");
              }}
              disabled={converting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConvertConfirm}
              disabled={!palletCode.trim() || converting}
            >
              {converting ? "Convirtiendo..." : "Convertir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
